import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, LogIn, FolderPlus, Pencil, ArrowLeftRight, FileDown, Activity } from "lucide-react";

const ICON = { login: LogIn, create: FolderPlus, update: Pencil, reassign: ArrowLeftRight, export: FileDown, action: Activity };
const TINT = { login: "#0E7C86", create: "#1FA45B", update: "#C20511", reassign: "#E8833A", export: "#0B1F3A", action: "#64748B" };

const rel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// Owner Activity Log: a unified, filterable per-user feed of actions across every licence
// (sign-ins, licence create/update, reassignments, portfolio exports). Navy/gold, zero-gap.
export function OwnerActivityLog() {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("all");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/activity", { params: { user_id: uid, limit: 200 } }); setEvents(data.events || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  return (
    <Card className="shadow-none overflow-hidden" data-testid="activity-log-card" style={{ borderColor: "#1e2a44" }}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" style={{ background: "#0B1420" }}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: "#FFFFFF" }}>Owner Activity Log</span>
          <Badge variant="outline" className="border-white/20 text-slate-200" data-testid="activity-count">{events.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Select value={uid} onValueChange={setUid}>
            <SelectTrigger className="h-8 w-64 bg-white/10 border-white/15 text-slate-100 focus:ring-[#C20511]" data-testid="activity-user-filter">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-200 hover:bg-white/10" onClick={load} title="Refresh" data-testid="activity-refresh"><RefreshCw size={14} /></Button>
        </div>
      </div>

      <div className="max-h-[560px] overflow-y-auto divide-y divide-border/60">
        {loading ? (
          <div className="px-4 py-10 text-center text-muted-foreground text-sm">Loading…</div>
        ) : events.length === 0 ? (
          <div className="px-4 py-10 text-center text-muted-foreground text-sm" data-testid="activity-empty">No activity recorded yet.</div>
        ) : events.map((e, i) => {
          const Ico = ICON[e.type] || Activity;
          return (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-accent/40" data-testid={`activity-row-${i}`}>
              <span className="grid place-items-center h-8 w-8 rounded-full shrink-0" style={{ background: `${TINT[e.type] || "#64748B"}1A`, color: TINT[e.type] || "#64748B" }}>
                <Ico size={15} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm leading-tight">
                  <span className="font-semibold text-foreground" data-testid={`activity-actor-${i}`}>{e.actor || "-"}</span>
                  <span className="text-muted-foreground"> {e.action}</span>
                  {e.target && <span className="font-medium text-foreground"> · {e.target}</span>}
                </p>
                {e.ip && <p className="text-[11px] text-muted-foreground font-mono">{e.ip}</p>}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap" title={e.at}>{rel(e.at)}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
